import React from 'react';
import { ReferralActivityDto } from 'common/api';
import { ReferralActivity } from 'global/referralActivity';
import { ReferralStatus, ReferralStatusType } from 'global/referralStatus';
import { FormattedMessage } from 'react-intl';
import { parseReferralStatus, referralStatusMapper } from 'utils/format';
import { Text } from '@clariness/clarikit-react';
import { Box } from '@clariness/box';

type EventDetails = {
  note?: string;
  referralStatus?: ReferralStatusType;
  scheduledDate?: string;
  timeZone: any;
};

export function displayActivityEvent(
  event: ReferralActivityDto.event,
  eventDetails: EventDetails
) {
  switch (event) {
    case ReferralActivity.STATUS_CHANGE: {
      const status = eventDetails.referralStatus;
      const parentStatus = parseReferralStatus(status);

      const scheduledDate = new Date(eventDetails.scheduledDate || '');

      return status === ReferralStatus.APPOINTMENT_SCHEDULED ? (
        <div style={{ whiteSpace: 'pre-line' }}>
          <FormattedMessage
            id="is.patient_details_modal.activity.appointment_scheduled_status_update"
            defaultMessage={`Status update - { parentStatus } { status }
                 Scheduled (on { date } at {time} )`}
            values={{
              parentStatus:
                parentStatus && parentStatus !== status ? (
                  <React.Fragment>
                    {referralStatusMapper[parentStatus].description}:
                  </React.Fragment>
                ) : (
                  ''
                ),
              status: eventDetails.referralStatus
                ? referralStatusMapper[eventDetails.referralStatus].description
                : '',
              date: `${scheduledDate.toLocaleDateString('de-DE', {
                timeZone: eventDetails.timeZone?.zoneName,
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })} `,
              time: `${scheduledDate.toLocaleTimeString('de-DE', {
                timeZone: eventDetails.timeZone?.zoneName,
                hour: 'numeric',
                minute: 'numeric',
              })} ${eventDetails.timeZone?.zoneName}`,
            }}
          />
        </div>
      ) : (
        <FormattedMessage
          id="is.patient_details_modal.activity.status_update"
          defaultMessage="Status update - { parentStatus } { status }"
          values={{
            parentStatus:
              parentStatus && parentStatus !== status ? (
                <React.Fragment>
                  {referralStatusMapper[parentStatus].description}:
                </React.Fragment>
              ) : (
                ''
              ),
            status: eventDetails.referralStatus
              ? referralStatusMapper[eventDetails.referralStatus]?.description
              : '',
          }}
        />
      );
    }
    case ReferralActivity.NOTE_CHANGE: {
      return eventDetails.note ? (
        <FormattedMessage
          id="is.patient_details_modal.activity.note_edit"
          defaultMessage="Note edited: <p>{ note }</p>"
          values={{
            note: eventDetails.note,
            p: (message: string) => (
              <Box
                sx={{
                  color: 'inherit',
                  fontStyle: 'italic',
                }}
              >
                <Text as="p" variant="bodySm">
                  {message}
                </Text>
              </Box>
            ),
          }}
        />
      ) : (
        <Box
          sx={{
            color: 'inherit',
            textDecoration: 'line-through',
          }}
        >
          <Text as="p" variant="bodySm">
            <FormattedMessage
              id="is.patient_details_modal.activity.note_deleted"
              defaultMessage="Note deleted"
            />
          </Text>
        </Box>
      );
    }
    case ReferralActivity.FORWARD_TO_SITE: {
      return (
        <FormattedMessage
          id="is.patient_details_modal.activity.forward_to_site"
          defaultMessage="Referral forwarded to site"
        />
      );
    }
    case ReferralActivity.VISIT_601_STATUS_CHANGE: {
      return (
        <FormattedMessage
          id="is.patient_details_modal.activity.visit_601_status_change"
          defaultMessage="601 visit marked as {status}"
          values={{
            status: eventDetails.note,
          }}
        />
      );
    }
    case ReferralActivity.SCREENER_PAUSED: {
      return (
        <FormattedMessage
          id="is.patient_details_modal.activity.screener_paused"
          defaultMessage="Screener paused"
        />
      );
    }
    case ReferralActivity.SEQSTER_MR_STATUS: {
      const message = {
        request_submitted: {
          id: 'is.patient_details_modal.activity.seqster_mr_request_submitted',
          defaultMessage: 'MR status: Request initiated',
        },
        request_resubmitted: {
          id: 'is.patient_details_modal.activity.seqster_mr_request_resubmitted',
          defaultMessage: 'MR Status: Requested again',
        },
        available: {
          id: 'is.patient_details_modal.activity.seqster_mr_available',
          defaultMessage: 'MR Status: Available',
        },
        not_found: {
          id: 'is.patient_details_modal.activity.seqster_mr_not_found',
          defaultMessage: 'MR Status: Not Found',
        },
      }[eventDetails.note || ''];

      return message ? <FormattedMessage {...message} /> : null;
    }
    case ReferralActivity.SEQSTER_MR_CONSENT: {
      return (
        <FormattedMessage
          id="is.patient_details_modal.activity.seqster_mr_consent"
          defaultMessage="MR Consent: Received"
        />
      );
    }
    case ReferralActivity.SEQSTER_MR_LINK_REQUESTED: {
      return (
        <FormattedMessage
          id="is.patient_details_modal.activity.seqster_mr_link_requested"
          defaultMessage="MR status: Link requested."
        />
      );
    }
    default:
      return null;
  }
}
